// src/utils/quoteUtils.js
import { getPricing } from '../services/pricingApi';

export const CONTRACT_DISCOUNTS = {
  12: 0.10,
  24: 0.15,
  36: 0.20
};

export const serviceData = {
  regions: {
        'ap-southeast-2': {
            name: 'Asia Pacific (Sydney)',
            region: 'ap-southeast-2'
        },
        'ap-southeast-4': {
            name: 'Asia Pacific (Melbourne)',
            region: 'ap-southeast-4'
        },
        'us-west-1': {
            name: 'US West (N. California)',
            region: 'us-west-1'
        },
        'us-west-2': {
            name: 'US West (Oregon)',
            region: 'us-west-2'
        },
        'us-east-1': {
            name: 'US East (N. Virginia)',
            region: 'us-east-1'
        },
        'us-east-2': {
            name: 'US East (Ohio)',
            region: 'us-east-2'
        }
  },
  instanceTypes: {
    't3.medium': {
        name: 'Normal Workload',
        storage: 250,
        instance: 't3.medium',
        useCase: 'Recommended starter server for up to 30 users and/or 1 account with minimal interfaces.',
      },
      't3.large': {
        name: 'Heavy Workload',
        storage: 250,
        instance: 't3.large',
        useCase: 'Powerful server for growing businesses up to 100 users and/or multiple interfaces and accounts.',
      },
      't3.xlarge': {
        name: 'Extra Heavy Workload',
        storage: 500,
        instance: 't3.xlarge',
        useCase: 'A server for demanding workloads with more than 100 users, multiple interfaces and accounts.',
      },
      // Add smaller instance types
    't3.small': {
        name: 'Small Workload',
        storage: 250,  // Adjust storage as appropriate
        instance: 't3.small',
        useCase: 'Suitable for low-traffic UAT/DR or small development environments.',
    },
    't3.micro': {
        name: 'Micro Workload',
        storage: 250, // Adjust storage as appropriate
        instance: 't3.micro',
        useCase: 'Suitable for very low-traffic UAT/DR or testing purposes.',
    },
  },
  supportTiers: {
      standard: {
          name: 'Standard (8x5)',
          baseMarkup: 0.15,
          minimumFee: 1500,
          description: 'Basic monitoring and 8x5 support',
          features: [
              'Business hours support (8x5)',
              'Basic monitoring and alerting',
              'Incident response during business hours',
              'Basic security monitoring'
          ]
      },
      professional: {
          name: 'Professional (16x5)',
          baseMarkup: 0.20,
          minimumFee: 2500,
          description: 'Enhanced monitoring and extended support',
          features: [
              'Extended support (16x5)',
              'Enhanced security monitoring',
              'Quarterly architecture reviews',
              'Priority incident response',
              'Everything in Standard tier'
          ]
      },
      enterprise: {
          name: 'Enterprise (24x7)',
          baseMarkup: 0.25,
          minimumFee: 3500,
          description: 'Full monitoring and 24x7 support',
          features: [
              '24x7 support',
              'Dedicated technical account manager',
              '15-minute response SLA for critical issues',
              'Monthly architecture reviews',
              'Advanced security and compliance monitoring',
              'Custom reporting',
              'Disaster recovery planning',
              'Everything in Professional tier'
          ]
      }
  },
  network: {
      vpn: { description: "VPN Connection" },
      nlb: { description: "Network Load Balancer" },
      alb: { description: "Application Load Balancer" },
      cognito: { description: "User Authentication Service" },
      cloudfront: { description: "Content Delivery Network" },
      natGateway: { description: "NAT Gateway" }
  },
  storage: {
      s3: { description: "S3 Storage" },
      ebs: { description: "EBS Storage" }
  }
};

export const fetchServicePrices = async (state) => {
  const prices = {};

  try {
    // --- Fetch EC2 Prices ---
    const ec2Response = await getPricing('ec2', {
      region: state.region,
      instanceType: state.workload,
      operatingSystem: 'RHEL'
    });
    prices.ec2 = ec2Response.price * 730;

    if (state.uatServer) {
      const uatEc2Response = await getPricing('ec2', {
        region: state.region,
        instanceType: state.uatWorkload, // Use uatWorkload
        operatingSystem: 'RHEL'
      });
      prices.uatEc2 = uatEc2Response.price * 730;
    }

    if (state.drServer) {
      const drEc2Response = await getPricing('ec2', {
        region: state.region,
        instanceType: state.drWorkload,  // Use drWorkload
        operatingSystem: 'RHEL'
      });
      prices.drEc2 = drEc2Response.price * 730;
    }

    // --- Rest of the price fetching (Load Balancers, NAT Gateway, S3, EBS, VPN) ---
    if (state.privateAlb || state.publicAlb) {
        const albResponse = await getPricing('loadbalancer', {
          region: state.region,
          loadBalancerType: 'alb'
        });
        prices.alb = albResponse.price * 730;
      }

      if (state.privateNlb || state.publicNlb) {
        const nlbResponse = await getPricing('loadbalancer', {
          region: state.region,
          loadBalancerType: 'nlb'
        });
        prices.nlb = nlbResponse.price * 730;
      }

    if (state.natGateways > 0) {
      const natResponse = await getPricing('natgateway', {
        region: state.region
      });
      prices.natGateway = natResponse.price * 730;
    }

    const s3Response = await getPricing('s3', {
        region: state.region,
        storageGb: 1024 // We're requesting the price for 1TB
      });
      prices.s3 = s3Response.pricePerGB * 1024; // Convert GB price to TB price
      console.log('S3 price per TB:', prices.s3);

    if (state.instanceStorage > 0) {
      const ebsResponse = await getPricing('ebs', {
        region: state.region,
        volumeType: 'gp3',
        sizeGb: state.instanceStorage
      });
      prices.ebs = ebsResponse.price;
    }
    
    // Add EBS price fetching for UAT
    if (state.uatServer && state.uatWorkload) {
      const uatEbsResponse = await getPricing('ebs', {
        region: state.region,
        volumeType: 'gp3',
        sizeGb: serviceData.instanceTypes[state.uatWorkload].storage
      });
      prices.uatEbs = uatEbsResponse.price;
    }
    
    // Add EBS price fetching for DR
    if (state.drServer && state.drWorkload) {
      const drEbsResponse = await getPricing('ebs', {
        region: state.region,
        volumeType: 'gp3',
        sizeGb: serviceData.instanceTypes[state.drWorkload].storage
      });
      prices.drEbs = drEbsResponse.price;
    }

    if (state.siteCount > 0) {
      const vpnResponse = await getPricing('vpn', {
        region: state.region
      });
      prices.vpn = vpnResponse.price *730;
    }
      return prices;
  } catch (error) {
    console.error('Error fetching prices:', error);
    throw error;
  }
};

export const calculateInfrastructureCosts = (state, exchangeRate, serviceData, prices) => {
  const instanceTypeDetails = serviceData.instanceTypes[state.workload];
  const uatInstanceTypeDetails = serviceData.instanceTypes[state.uatWorkload]; // Get UAT details
  const drInstanceTypeDetails = serviceData.instanceTypes[state.drWorkload];   // Get DR details

  let natGatewayCount = 0;
  let natGatewayDescription = "No NAT Gateway required";

  if (state.requiresInternet) {
    natGatewayCount = state.drServer ? 2 : 1;
    natGatewayDescription = state.drServer
      ? `${natGatewayCount} NAT Gateways for Production and DR`
      : "NAT Gateway for Production";
  }

  const infrastructure = {
    costs: {
      environment: {
        production: {
          cost: prices.ec2 * exchangeRate,
          description: instanceTypeDetails
            ? `${instanceTypeDetails.name} (${instanceTypeDetails.instance})`
            : "Production server environment",
        },
        uat: {
          cost: state.uatServer ? prices.uatEc2 * exchangeRate : 0, // Use uatEc2 price
          description: state.uatServer
            ? `${uatInstanceTypeDetails ? uatInstanceTypeDetails.name : 'UAT'} Server (${uatInstanceTypeDetails ? uatInstanceTypeDetails.instance : 'same as production'})`
            : "No UAT environment",
        },
        dr: {
          cost: state.drServer ? prices.drEc2 * exchangeRate : 0,  // Use drEc2 price
          description: state.drServer
            ? `${drInstanceTypeDetails ? drInstanceTypeDetails.name : 'DR'} Server (${drInstanceTypeDetails ? drInstanceTypeDetails.instance : 'same as production'})`
            : "No DR environment",
        },
      },
      network: {
        vpn: {
          cost: state.siteCount > 0
            ? (state.centralizedVpn ? prices.vpn : prices.vpn * state.siteCount) * exchangeRate
            : 0,
          description: state.centralizedVpn ? "Centralized VPN Hub" : `VPN Connections for ${state.siteCount} sites`
        },
        privateNlb: {
          cost: state.privateNlb ? prices.nlb * exchangeRate : 0,
          description: "Private Network Load Balancer"
        },
        publicNlb: {
          cost: state.publicNlb ? prices.nlb * exchangeRate : 0,
          description: "Internet Facing Network Load Balancer"
        },
        privateAlb: {
          cost: state.privateAlb ? prices.alb * exchangeRate : 0,
          description: "Private Application Load Balancer"
        },
        publicAlb: {
          cost: state.publicAlb ? prices.alb * exchangeRate : 0,
          description: "Internet Facing Application Load Balancer"
        },
        natGateway: {
          cost: prices.natGateway * natGatewayCount * exchangeRate,
          description: natGatewayDescription
        },
        // cognito: {
        //   cost: (state.privateAlb || state.publicAlb) ? prices.cognito * exchangeRate : 0,
        //   description: "User Authentication Service"
        // },
        // cloudfront: {
        //   cost: (state.privateAlb || state.publicAlb) ? prices.cloudfront * exchangeRate : 0,
        //   description: "Content Delivery Network"
        // }
      },
      storage: {
        s3Storage: {
          cost: 2 * prices.s3 * exchangeRate, // Always calculate for 2TB
          description: "2TB S3 Storage"
        },
        instanceStorage: {
          cost: serviceData.instanceTypes[state.workload].storage * prices.ebs * exchangeRate,
          description: `${serviceData.instanceTypes[state.workload].storage}GB Production EBS Storage`
        },
        uatStorage: {
          cost: state.uatServer && state.uatWorkload ? 
            serviceData.instanceTypes[state.uatWorkload].storage * (prices.uatEbs || prices.ebs) * exchangeRate : 0,
          description: state.uatServer && state.uatWorkload ? 
            `${serviceData.instanceTypes[state.uatWorkload].storage}GB UAT EBS Storage` : "No UAT Storage"
        },
        drStorage: {
          cost: state.drServer && state.drWorkload ? 
            serviceData.instanceTypes[state.drWorkload].storage * (prices.drEbs || prices.ebs) * exchangeRate : 0,
          description: state.drServer && state.drWorkload ? 
            `${serviceData.instanceTypes[state.drWorkload].storage}GB DR EBS Storage` : "No DR Storage"
        }
      }
    },
    total: 0
  };

  let infraTotal = 0;
  Object.values(infrastructure.costs).forEach(category => {
    Object.values(category).forEach(item => {
      infraTotal += item.cost;
    });
  });

  infrastructure.total = infraTotal;
  return infrastructure;
};

export const calculateSupportCosts = (infrastructureTotal, state, serviceData) => {
    const tier = serviceData.supportTiers[state.supportTier];
    const infraTotal = parseFloat(infrastructureTotal) || 0;
    const tierFeeBeforeDiscount = Math.max(infraTotal * tier.baseMarkup, tier.minimumFee);
  
    const discount = CONTRACT_DISCOUNTS[state.contractLength] || 0;
    const discountAmount = tierFeeBeforeDiscount * discount;
    const tierFee = tierFeeBeforeDiscount - discountAmount;
  
    return {
      costs: {
        included: tier.features,
      },
      total: tierFee,
      totalBeforeDiscount: tierFeeBeforeDiscount,
      discountPercentage: (discount * 100).toFixed(0),
      discountAmount: discountAmount,
      tierDetails: {
        name: tier.name,
        baseMarkup: tier.baseMarkup,
        minimumFee: tier.minimumFee,
      },
    };
};

export const calculateSummary = (infrastructureTotal, supportCosts) => {
  const infraTotal = parseFloat(infrastructureTotal) || 0;
  const supportTotal = parseFloat(supportCosts.total) || 0;
  const supportTotalBeforeDiscount = parseFloat(supportCosts.totalBeforeDiscount) || 0;
  const discountAmount = parseFloat(supportCosts.discountAmount) || 0;
  // const implementation = parseFloat(implementationFee) || 0; // REMOVED

  const subtotal = infraTotal + supportTotalBeforeDiscount;
  const finalTotal = infraTotal + supportTotal; // Removed implementationFee

  return {
    subtotal: subtotal.toFixed(2),
    supportTotalBeforeDiscount: supportTotalBeforeDiscount.toFixed(2),
    supportDiscountPercentage: supportCosts.discountPercentage || '0',
    supportDiscountAmount: discountAmount.toFixed(2),
    finalTotal: finalTotal.toFixed(2),
    // implementationFee: implementation.toFixed(2) // REMOVED
  };
};
